import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'profile' | 'article';
  noindex?: boolean;
  locale?: 'fr' | 'en';
}

export const SEOHead = ({
  title,
  description,
  keywords = [],
  image = '/og-images/og-default.png',
  url,
  type = 'website',
  noindex = false,
  locale = 'fr'
}: SEOHeadProps) => {
  const location = useLocation();
  const baseUrl = 'https://firsttuesday.dz';
  const currentUrl = url || `${baseUrl}${location.pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords.join(', ') },
      { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
      
      // Open Graph
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: fullImageUrl },
      { property: 'og:url', content: currentUrl },
      { property: 'og:type', content: type },
      { property: 'og:locale', content: locale === 'fr' ? 'fr_DZ' : 'en_US' },
      { property: 'og:site_name', content: 'First Tuesday' },
      
      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: fullImageUrl },
      
      // Geographic
      { name: 'geo.region', content: 'DZ-16' },
      { name: 'geo.placename', content: 'Alger' },
      { name: 'geo.position', content: '36.7538;3.0588' }
    ];

    metaTags.forEach(({ name, property, content }) => {
      const attribute = name ? 'name' : 'property';
      const value = name || property;
      
      let element = document.querySelector(`meta[${attribute}="${value}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, value!);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Hreflang tags
    const hreflangs = [
      { lang: 'fr', href: baseUrl + location.pathname },
      { lang: 'en', href: `${baseUrl}/en${location.pathname}` },
      { lang: 'x-default', href: baseUrl + location.pathname }
    ];

    // Remove existing hreflang tags
    document.querySelectorAll('link[rel="alternate"]').forEach(el => el.remove());

    // Add new hreflang tags
    hreflangs.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });

  }, [title, description, keywords, fullImageUrl, currentUrl, type, noindex, locale, location.pathname]);

  return null;
};
